
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { updateListBackground } from "@/services/shopping/lists/background.service";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useModalStore } from "@/services/modalStore";
import { useModalBackButton } from "@/hooks/useModalBackButton";
import usePremiumFeatures from "@/hooks/usePremiumFeatures";
import { toast } from "sonner";

interface BackgroundColorPickerProps {
  listId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentColor?: string;
}

// Definizione dei colori predefiniti
const PREDEFINED_COLORS = [
  { name: 'Rosso', value: 'linear-gradient(135deg, #f44336, #e57373)' },
  { name: 'Rosa', value: 'linear-gradient(135deg, #e91e63, #f06292)' },
  { name: 'Viola', value: 'linear-gradient(135deg, #9c27b0, #ba68c8)' },
  { name: 'Indaco', value: 'linear-gradient(135deg, #3f51b5, #7986cb)' },
  { name: 'Blu', value: 'linear-gradient(135deg, #2196f3, #64b5f6)' },
  { name: 'Verde', value: 'linear-gradient(135deg, #4caf50, #81c784)' },
  { name: 'Lime', value: 'linear-gradient(135deg, #cddc39, #d4e157)' },
  { name: 'Giallo', value: 'linear-gradient(135deg, #ffeb3b, #fff176)' },
  { name: 'Ambra', value: 'linear-gradient(135deg, #ffc107, #ffd54f)' },
  { name: 'Arancione', value: 'linear-gradient(135deg, #ff9800, #ffb74d)' },
  { name: 'Marrone', value: 'linear-gradient(135deg, #795548, #a1887f)' },
  { name: 'Grigio', value: 'linear-gradient(135deg, #9e9e9e, #bdbdbd)' },
  { name: 'Ciano', value: 'linear-gradient(135deg, #00bcd4, #4dd0e1)' },
  { name: 'Turchese', value: 'linear-gradient(135deg, #1de9b6, #00bfa5)' },
  { name: 'Trasparente', value: '' }
];



const BackgroundColorPicker = ({ 
  listId, 
  isOpen, 
  onOpenChange, 
  currentColor = "" 
}: BackgroundColorPickerProps) => {
  const [selectedColor, setSelectedColor] = useState(currentColor);
  const queryClient = useQueryClient();
  const { toast: uiToast } = useToast();
  const { theme } = useThemeConfig();
  const modalStore = useModalStore();
  const { tryUseFeature } = usePremiumFeatures();
  
  // Fix: Only register the modal once when the component mounts
  useEffect(() => {
    if (isOpen) {
      modalStore.openModal("background-color-picker-dialog");
      
      // Verifica se l'utente ha accesso alla funzionalità premium
      if (!tryUseFeature('list_backgrounds', {
        message: "La personalizzazione dello sfondo delle liste è disponibile solo con l'abbonamento Premium",
        showToast: true
      })) {
        handleOpenChange(false);
      }
    }
    // Remove dependency on isOpen to prevent infinite loop
  }, [isOpen]);
  
  const { handleOpenChange } = useModalBackButton({
    isOpen,
    modalName: "background-color-picker-dialog",
    onOpenChange: (openValue) => {
      if (!openValue) {
        modalStore.closeModal();
      }
      onOpenChange(openValue);
    },
  });

  const handleSave = async () => {
    // Verifica nuovamente se l'utente ha accesso alla funzionalità premium
    if (!tryUseFeature('list_backgrounds', {
      showToast: true
    })) {
      return;
    }
    
    try {
      await updateListBackground(listId, selectedColor);
      
      // Invalidare le query dopo aver salvato e PRIMA di chiudere il dialog
      await queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
      await queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
      
      uiToast({
        description: "Colore sfondo aggiornato",
        variant: "success"
      });
      
      // Aspettiamo che le query siano invalidate prima di chiudere
      setTimeout(() => {
        handleOpenChange(false);
      }, 100);
      
    } catch (error) {
      console.error("Error updating background:", error);
      uiToast({
        title: "Errore",
        description: "Impossibile aggiornare il colore di sfondo",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        style={{
          borderRadius: theme.borderRadius,
          backgroundColor: theme.backgroundColor,
          color: theme.textColor,
          transition: `all ${theme.transitionSpeed} ease`,
        }}
      >
        <DialogHeader>
          <DialogTitle>Scegli colore sfondo</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-3 gap-3 py-4">
          {PREDEFINED_COLORS.map((color) => (
            <div
              key={color.name}
              className="flex flex-col items-center space-y-0 cursor-pointer"
            >
              <span
                className="text-xs font-medium"
                style={{
                  color: theme.textColor,
                  textShadow: color.value ? '0 1px 2px rgba(0,0,0,0.4)' : 'none'
                }}
              >
                {color.name}
              </span>
              <div
                className="w-full border-2 rounded-lg hover:opacity-90"
                style={{
                  background: color.value || '#ffffff',
                  height: '50px',
                  borderRadius: theme.borderRadius,
                  borderColor:
                    selectedColor === color.value
                      ? "#000000"
                      : theme.borderColor,
                  borderWidth: selectedColor === color.value ? '3px' : '0px',
                }}
                onClick={() => setSelectedColor(color.value)}
              />
            </div>
          ))}
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
              style={{
                backgroundColor: theme.backgroundColor,                
                color: theme.textColor,                
                borderRadius: theme.buttonBorderRadius,
                padding: theme.buttonPadding,
                fontWeight: theme.buttonFontWeight,
                transitionDuration: theme.transitionSpeed,
                '--btn-bg-hover': theme.primaryColor,
              } as React.CSSProperties}
          >
            Annulla
          </Button>
        <Button
          variant="default"
          className="w-full hover:bg-[var(--btn-bg-hover)]"
          onClick={handleSave}
          style={{
            backgroundColor: theme.primaryColor,
            color: theme.textPrimaryColor,
            fontWeight: 'bold',
            borderRadius: theme.buttonBorderRadius,
            padding: theme.buttonPadding,
            transition: `all ${theme.transitionSpeed || '0.2s'} ease`,
            '--btn-bg-hover': theme.primaryColor,
          } as React.CSSProperties}
          >
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BackgroundColorPicker;
